import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { storage } from "@/lib/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";

const useImageUpload = (maxImages = 5, maxSizeMB = 5) => {
  const [images, setImages] = useState([]);
  const { toast } = useToast();

  const setInitialImages = useCallback((initialImages) => {
    setImages(initialImages);
  }, []);

  const handleImageChange = useCallback(
    (e) => {
      const files = Array.from(e.target.files);

      if (images.length + files.length > maxImages) {
        toast({
          variant: "destructive",
          title: "Image Limit Exceeded",
          description: `You can upload a maximum of ${maxImages} images.`
        });
        return;
      }

      const imageFiles = files.filter((file) => file.type.startsWith("image/"));
      if (imageFiles.length !== files.length) {
        toast({
          variant: "destructive",
          title: "Invalid File Type",
          description: "Please upload only image files."
        });
      }

      const validFiles = imageFiles.filter((file) => {
        if (file.size > maxSizeMB * 1024 * 1024) {
          toast({
            variant: "destructive",
            title: "File Too Large",
            description: `${file.name} exceeds the ${maxSizeMB}MB limit.`
          });
          return false;
        }
        return true;
      });

      const newImages = validFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        isExisting: false
      }));

      setImages((prev) => [...prev, ...newImages]);
      e.target.value = null;
    },
    [images, maxImages, maxSizeMB, toast]
  );

  const removeImage = useCallback((indexToRemove) => {
    setImages((prev) => {
      const imageToRemove = prev[indexToRemove];
      if (imageToRemove && imageToRemove.preview && !imageToRemove.isExisting) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      return prev.filter((_, index) => index !== indexToRemove);
    });
  }, []);

  const resetImages = useCallback(() => {
    images.forEach((img) => {
      if (img.preview && !img.isExisting) {
        URL.revokeObjectURL(img.preview);
      }
    });
    setImages([]);
  }, [images]);

  const uploadImages = useCallback(
    async (sellerId) => {
      if (images.length === 0) return [];
      if (!sellerId) {
        throw new Error("Seller ID is required to upload images.");
      }

      const existingImageUrls = images
        .filter((img) => img.isExisting)
        .map((img) => img.url);

      const newImages = images.filter((img) => !img.isExisting);

      const uploadPromises = newImages.map((image) => {
        const imageRef = ref(
          storage,
          `parts/${sellerId}/${uuidv4()}-${image.file.name}`
        );
        return uploadBytes(imageRef, image.file).then((snapshot) =>
          getDownloadURL(snapshot.ref)
        );
      });

      try {
        const newUploadedUrls = await Promise.all(uploadPromises);
        return [...existingImageUrls, ...newUploadedUrls];
      } catch (error) {
        console.error("Error uploading images to Firebase Storage:", error);
        toast({
          variant: "destructive",
          title: "Image Upload Failed",
          description: "Could not upload images. Please try again."
        });
        throw error;
      }
    },
    [images, toast]
  );

  useEffect(() => {
    return () => {
      images.forEach((img) => {
        if (img.preview && !img.isExisting) {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, [images]);

  return {
    images,
    handleImageChange,
    removeImage,
    resetImages,
    uploadImages,
    setInitialImages,
    imageCount: images?.length
  };
};

export default useImageUpload;
